import React from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import type { DashboardOverviewAnalyticsResponse } from '@/lib/api/dashboard';

import { CS_PRIMARY } from './dashboardHomeTokens';
import { analyticsRevenueByMonth } from './dashboardAnalyticsData';
import { DashboardCollectedLeafDecoration } from './DashboardCollectedLeafDecoration';
import { formatDashboardCurrency } from './dashboardHomeFormatters';

const REV_GRADIENT_ID = 'dashboardAnalyticsRevenueFill';

/** Same Invoices & Billing logo used in Today tab card header. */
const InvoiceBillingLogoBlue = () => (
  <Box
    component="svg"
    width={24}
    height={24}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
    sx={{ display: 'block' }}
  >
    <path
      opacity={0.5}
      fillRule="evenodd"
      clipRule="evenodd"
      d="M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
      fill={CS_PRIMARY}
    />
    <path
      d="M12.75 6C12.75 5.58579 12.4142 5.25 12 5.25C11.5858 5.25 11.25 5.58579 11.25 6V6.31673C9.61957 6.60867 8.25 7.83361 8.25 9.5C8.25 11.4172 10.0628 12.75 12 12.75C13.3765 12.75 14.25 13.6557 14.25 14.5C14.25 15.3443 13.3765 16.25 12 16.25C10.6235 16.25 9.75 15.3443 9.75 14.5C9.75 14.0858 9.41421 13.75 9 13.75C8.58579 13.75 8.25 14.0858 8.25 14.5C8.25 16.1664 9.61957 17.3913 11.25 17.6833V18C11.25 18.4142 11.5858 18.75 12 18.75C12.4142 18.75 12.75 18.4142 12.75 18V17.6833C14.3804 17.3913 15.75 16.1664 15.75 14.5C15.75 12.5828 13.9372 11.25 12 11.25C10.6235 11.25 9.75 10.3443 9.75 9.5C9.75 8.65573 10.6235 7.75 12 7.75C13.3765 7.75 14.25 8.65573 14.25 9.5C14.25 9.91421 14.5858 10.25 15 10.25C15.4142 10.25 15.75 9.91421 15.75 9.5C15.75 7.83361 14.3804 6.60867 12.75 6.31673V6Z"
      fill={CS_PRIMARY}
    />
  </Box>
);

export interface DashboardAnalyticsRevenuePanelProps {
  data?: DashboardOverviewAnalyticsResponse;
  isLoading?: boolean;
  onViewInvoices?: () => void;
}

export const DashboardAnalyticsRevenuePanel: React.FC<DashboardAnalyticsRevenuePanelProps> = ({
  data,
  isLoading = false,
  onViewInvoices,
}) => {
  const revenue = data?.revenue;
  const currency = data?.cards.monthlyRevenue.currency;
  const chartData = revenue?.chart?.length
    ? revenue.chart.map((point) => ({ month: point.label, value: point.amount }))
    : analyticsRevenueByMonth;
  const delta = revenue?.deltaPercent ?? 0;
  const DeltaIcon = delta >= 0 ? ArrowUpwardRoundedIcon : ArrowDownwardRoundedIcon;
  const valueText = isLoading && !data ? '--' : formatDashboardCurrency(revenue?.amount, currency);
  const previousText = formatDashboardCurrency(revenue?.previousMonthAmount, currency);
  const collectedText = isLoading && !data ? '--' : formatDashboardCurrency(revenue?.collected, currency);
  const pendingText = isLoading && !data ? '--' : formatDashboardCurrency(revenue?.pending, currency);
  const isCustomRange = Boolean(data?.period.isCustomRange);
  const previousLabel = isCustomRange ? 'previous range' : 'last month';
  const hasPaymentInterval = typeof revenue?.paymentIntervalDaysAvg === 'number';

  return (
    <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid #ebebeb',
        overflow: 'hidden',
      }}
    >
      <Stack sx={{ px: 2.5, pt: 1.5, pb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pb: 1.5, borderBottom: '1px solid #f2f2f2' }}>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <InvoiceBillingLogoBlue />
            <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a' }}>Revenue</Typography>
          </Stack>
          <Button
            variant="text"
            size="small"
            onClick={onViewInvoices}
            endIcon={<ChevronRightRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              fontSize: 14,
              color: CS_PRIMARY,
              minWidth: 0,
              px: 0.5,
              '&:hover': { bgcolor: 'rgba(30, 174, 219, 0.06)' },
            }}
          >
            View invoices
          </Button>
        </Stack>

        <Stack spacing={0.625} sx={{ mt: 2, mb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography sx={{ fontWeight: 500, fontSize: 24, color: '#111', letterSpacing: '-0.03em' }}>{valueText}</Typography>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ bgcolor: 'rgba(0, 187, 167, 0.13)', borderRadius: 999, px: 1, py: 0.5 }}
            >
              <DeltaIcon sx={{ fontSize: 12, color: '#0a0a0a' }} />
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#0a0a0a' }}>{Math.abs(delta)}%</Typography>
            </Stack>
          </Stack>
          <Typography sx={{ fontSize: 12, fontWeight: 400, color: '#000' }}>vs {previousText} {previousLabel}</Typography>
        </Stack>

        <Box sx={{ width: '100%', height: 120, mt: 0.5 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id={REV_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00bba7" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#00bba7" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#000', fontSize: 13 }}
                dy={8}
              />
              <YAxis hide domain={['dataMin - 500', 'dataMax + 500']} />
              <Tooltip
                cursor={{ stroke: 'rgba(0,187,167,0.2)' }}
                contentStyle={{ borderRadius: 8, border: '1px solid #ebebeb', fontSize: 12, color: '#000' }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#00bba7"
                strokeWidth={2}
                fill={`url(#${REV_GRADIENT_ID})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </Stack>
    </Paper>

    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap>
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: '16px',
          border: '1px solid #ebebeb',
          background: `linear-gradient(180deg, ${CS_PRIMARY} 0%, #115d76 180%)`,
          color: '#fff',
          py: 2,
          px: 2,
          minHeight: 82.5,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <DashboardCollectedLeafDecoration
          sx={{
            right: -6,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />
        <Typography sx={{ position: 'relative', zIndex: 1, fontWeight: 500, fontSize: 12, color: '#fff' }}>Collected</Typography>
        <Typography sx={{ position: 'relative', zIndex: 1, fontWeight: 500, fontSize: 20, mt: 0.5, letterSpacing: '-0.03em', color: '#fff' }}>
          {collectedText}
        </Typography>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: '16px',
          border: '1px solid rgba(0, 187, 167, 0.62)',
          py: 2,
          px: 2,
          minHeight: 82.5,
        }}
      >
        <Typography sx={{ fontWeight: 400, fontSize: 12, color: '#000' }}>Payment Interval</Typography>
        <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mt: 0.5 }}>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: hasPaymentInterval ? 20 : 16,
              color: '#00bba7',
              letterSpacing: 0,
              lineHeight: 1.25,
            }}
          >
            {hasPaymentInterval ? revenue.paymentIntervalDaysAvg : 'N/A'}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#000', fontWeight: 400 }}>days on avg.</Typography>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: '16px',
          border: '1px solid #ffa2a2',
          py: 2,
          px: 2,
          minHeight: 82.5,
        }}
      >
        <Typography sx={{ fontWeight: 400, fontSize: 12, color: '#000' }}>Pending</Typography>
        <Typography sx={{ fontWeight: 500, fontSize: 20, color: '#ffa2a2', mt: 0.5, letterSpacing: '-0.03em' }}>
          {pendingText}
        </Typography>
      </Paper>
    </Stack>
    </Stack>
  );
};
